import { bridge } from "@multiweb/reactCore/bridge";
import { IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { IOrderBy, OrderType } from "@multiweb/types/multiweb";
import { EncryptedRecordType, ReactAction, RecordType, TypedObject } from "@multiweb/types/react";
import { CLICKABLE_ROW_ACTION_MOCK } from "./constants";

const { dateToString } = bridge;

/**
 * Slouží k zobrazení akcí.
 * @param actions Zobrazí zadané akce.
 * @param clickableRow Pokud je pravda, přidá akci sloužící ke kliknutí na řádek.
 */
export const displayActions = (actions: ReactAction[], clickableRow: boolean) =>
    clickableRow ? actions.concat(CLICKABLE_ROW_ACTION_MOCK) : actions;

/**
 * Vrací n-tý prvek z pole. Pokud je index větší než délka pole jako index bere zbytek po dělení délkou pole.
 *
 * @example
 * const names = ["John", "Mike", "Monica", "Jane"];
 * getNth(3, names); // "Jane"
 * getNth(4, names); // "John"
 * getNth(40, names); // "John"
 *
 * @param array Pole, ze kterého bereme prvek.
 * @param index Index prvku.
 */
export const getNthItem = <T>(array: ReadonlyArray<T>, index: number): T | undefined => {
    if (array.length === 0) {
        return undefined;
    }
    const realIndex = index % array.length;
    return array[realIndex];
};

/**
 * Vrátí pole vyfiltrovaných dat.
 * @param data Data pro filtrování.
 * @param attributesInfo Informace o atributech.
 * @param filterAttributesNames Pole názvů atributů, podle kterých se má filtrovat.
 * @param condition Podmínka pro filtrování.
 */
export const filterData = (
    data: RecordType[],
    attributesInfo: TypedObject<IPublicAttributeInfo>,
    filterAttributesNames: ReadonlyArray<string>,
    condition: string,
): RecordType[] => {
    const fulltextAttributesInfo: IPublicAttributeInfo[] = filterAttributesNames.map(
        (attributeName) => attributesInfo[attributeName],
    );

    return data.filter((obj) => {
        for (const { attributeName, type } of fulltextAttributesInfo) {
            let found = false;
            switch (type) {
                case OWDataType.date: {
                    if (!(obj[attributeName].value instanceof Date)) {
                        continue;
                    }
                    found = dateToString(obj[attributeName].value).split(" ")[0].includes(condition);
                    break;
                }
                case OWDataType.time: {
                    if (!(obj[attributeName].value instanceof Date)) {
                        continue;
                    }
                    found = dateToString(obj[attributeName].value).split(" ")[1].includes(condition);
                    break;
                }
                case OWDataType.dateTime: {
                    if (!(obj[attributeName].value instanceof Date)) {
                        continue;
                    }
                    found = dateToString(obj[attributeName].value).includes(condition);
                    break;
                }
                default: {
                    found = obj[attributeName].value?.toString().toLowerCase().includes(condition);
                }
            }
            if (found) {
                return true;
            }
        }
        return false;
    });
};

/**
 * Vrátí pole seřazených dat.
 *
 * @param data Data pro seřazení (funkce vstupní pole neovlivní - vrací pole nové).
 * @param orderBy Pole atributů pro seřazení a druhů řazení.
 */
export const orderData = (data: RecordType[], orderBy: ReadonlyArray<IOrderBy>) =>
    [...data].sort((a, b) => {
        for (const { attributeName, order } of orderBy) {
            const first = a[attributeName]?.value;
            const second = b[attributeName]?.value;
            if (first === second) {
                continue;
            } else {
                const result = first && second ? (first < second ? -1 : 1) : first ? -1 : 1;
                return order === OrderType.ascending ? result : -result;
            }
        }
        return 0;
    });

/**
 * Převede pole na TypedObject, identifikátory získá pomocí funkce getId.
 *
 * @param reducedArray Pole, které převede na TypedObject.
 * @param getId Funkce, která získá identifikátor z objektu.
 * @returns TypedObject s namapovanými prvky z pole pod indentifikátory získaných pomocí funkce getId.
 */
export const reduceArrayToTypedObject = <T>(
    reducedArray: ReadonlyArray<T>,
    getId: (value: T) => string,
): TypedObject<T> => reducedArray.reduce(
        (acc, value) => ({
            ...acc,
            [getId(value).toLowerCase()]: value,
        }),
        {},
    );

/**
 * Vytvoří nový typovaný objekt, kde je pod zadanými identifikátory zadaná hodnota.
 *
 * @param idsArray Pole indentifikátorů pro které se má nastavit zadaná hodnota.
 * @param value Hodnota přiřazená pod identifikátory.
 */
export const setAllObjectPropertiesFromIdsArray = <T>(idsArray: ReadonlyArray<string>, value: T): TypedObject<T> =>
    idsArray.reduce((acc, id) => ({ ...acc, [id]: value }), {});

/**
 * Vytvoří záznam pro data grid
 * @param properties objekt typu klíč:hodnota, který definuje atributy a hodnoty vytvářeného záznamu.
 * @param isRecordEncrypted Informace, zda je záznam zašifrovaný.
 */
export const createRecord = (properties: Record<string, unknown>, isRecordEncrypted?: boolean): RecordType | EncryptedRecordType => {
    const keyValuesTempObject = {};
    Object.keys(properties).forEach(k => Reflect.set(keyValuesTempObject, k, { value: properties[k] }));
    if (isRecordEncrypted) {
        return { ...keyValuesTempObject, encrypted: { value: true } } as EncryptedRecordType;
    } else {
        return { ...keyValuesTempObject } as RecordType;
    }
};

/**
 * Vytvoří objekt, který popisuje vlastnosti atributu Např. jeho název, datový typ, atd.
 * @param attributeName Název atributu
 * @param type Datový typ atributu
 * @param encryptingAlowed Určuje, jestli podporuje šifrování.
 */
export const createPublicAttributeInfo = (attributeName: string, type: OWDataType = OWDataType.string, encryptingAlowed?: boolean): IPublicAttributeInfo => {
    return {
        attributeName,
        type,
        encryptingAlowed,
    };
};
